import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useSignupApi } from './useSignupApi';
import type { SignupApi, SignupSponsor } from '../services/signup-api/signup-api';
import {
  isRetryableSponsorFailure,
  SPONSOR_LOOKUP_RETRY_DELAY_MS,
} from '../services/sponsor-lookup';

/** The sponsor lookup's cache key, for readers that only look in the cache. */
export function signupSponsorQueryKey(
  scenario: string | null,
  clientId: string | undefined,
  subClientId: string | undefined,
) {
  return ['signup', 'sponsor', scenario, clientId ?? null, subClientId ?? null] as const;
}

/**
 * The sponsor lookup as one query, so a screen that reads the sponsor ahead of
 * navigating shares its answer with every screen that renders it later.
 */
export function signupSponsorQuery(
  api: SignupApi,
  scenario: string | null,
  clientId: string | undefined,
  subClientId: string | undefined,
) {
  return queryOptions({
    queryKey: signupSponsorQueryKey(scenario, clientId, subClientId),
    queryFn: () => api.resolveSponsor({ clientId, subClientId }),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (failures: number, error: Error) => failures < 1 && isRetryableSponsorFailure(error),
    retryDelay: SPONSOR_LOOKUP_RETRY_DELAY_MS,
  });
}

/**
 * The sponsor a landing link names, through the signup data seam. The answer
 * is kept for the page session, so every screen after the first reuses it:
 * one member costs the per-network lookup limit one lookup per page load, and
 * a later refusal cannot take a resolved sponsor away. A refusal or failure
 * that may be momentary is retried once, quietly; anything else fails at once,
 * and a screen mounting after a failure reads that failure rather than asking
 * again.
 */
export function useSignupSponsor(
  clientId: string | undefined,
  subClientId: string | undefined,
  enabled = true,
): UseQueryResult<SignupSponsor> {
  const api = useSignupApi();
  const scenario = useSearchParams().get('scenario');
  return useQuery({
    ...signupSponsorQuery(api, scenario, clientId, subClientId),
    retryOnMount: false,
    enabled,
  });
}
